"use client";

import { useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { secToHourMin } from "@/helpers/secToMinS";
import { Card, CardContent } from "../ui/card";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import CourseCardMenu from "./CourseCardMenu";
import ArchivedCoursesModal from "./ArchivedCoursesModal";

// Utilitaire pour formater le temps
const formatTime = (seconds, t) => {
    if (!seconds) return t("time_format_no_time");
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
        return t("time_format_hours_minutes", { hours, minutes });
    } else {
        return t("time_format_minutes_only", { minutes });
    }
};

// Composant pour la formation principale
const MainCourseCard = ({ course, onArchiveChange }) => {
    const t = useTranslations("CourseSection");
    const courseData = course.product || course.program;
    const progression = course.progression;
    const progressPercentage = progression?.total || 0;
    const isAccelerator = !course.product;
    const isCompleted = progressPercentage === 100;

    // Séance à reprendre, calculée par le back : la première non validée du
    // sommaire. La progression, elle, pointe sur la dernière séance TERMINÉE :
    // s'en servir renverrait l'utilisateur sur une vidéo qu'il vient de voir.
    const nextSession = progression?.next;

    let link;
    // Les programmes et accélérateurs ouvrent un sommaire, pas une vidéo :
    // annoncer une séance à reprendre y serait un mensonge.
    let linksToSession = false;
    if (
        courseData &&
        courseData.programConstructorVersion &&
        courseData.programConstructorVersion >= 2
    ) {
        // For program with constructor structure version >= 2
        link = `/accelerators/${courseData.slug}/dashboard`;
    } else if (course.category == "program") {
        if (course.slug == "le-programme-rocket-2020") {
            linksToSession = true;
            if (nextSession) {
                link = `/course/${courseData.slug}/${nextSession.part}/${nextSession.position}`;
            } else if (progression && progression.part) {
                link = `/course/${courseData.slug}/${progression.part}/${progression.position}`;
            } else {
                link = `/course/${courseData.slug}/1/1`;
            }
        } else {
            link = `/course/${courseData.slug}/dashboard`;
        }
    } else {
        linksToSession = true;
        if (nextSession) {
            link = `/course/${courseData.slug}/${nextSession.part}/${nextSession.position}`;
        } else if (progression && progression.part) {
            link = `/course/${courseData.slug}/${progression.part}/${progression.position}`;
        } else {
            link = `/course/${courseData.slug}/1/1`;
        }
    }

    const showNextSession = Boolean(nextSession) && linksToSession;
    const actionLabel = progressPercentage > 0 ? t("continue") : t("start");

    return (
        <Card className={`special-card group relative ${isCompleted ? "overflow-hidden" : ""}`}>
            <Link href={link} className="block">
                <CardContent className="pt-5 pb-4 lg:pb-6 relative">
                    {isCompleted && (
                        <div
                            className="absolute inset-0 pointer-events-none opacity-[0.03]"
                            style={{
                                backgroundImage: "repeating-linear-gradient(45deg, currentColor, currentColor 1px, transparent 1px, transparent 8px)"
                            }}
                        />
                    )}
                    <div className="flex items-center gap-4 mb-4 relative pr-12 lg:pr-44">
                        <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0">
                            <Image
                                src={courseData?.image}
                                alt={courseData?.name}
                                width={48}
                                height={48}
                                className="object-cover w-full h-full"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-lg leading-tight group-hover:text-black group-hover:dark:text-white transition-colors">
                                {courseData?.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-2">
                                <span className="text-sm text-gray-500 dark:text-grayDark-400">
                                    {progressPercentage}% •{" "}
                                    {t("minutesConsumed", {
                                        time: formatTime(
                                            progression.secondsConsumed,
                                            t,
                                        ),
                                    })}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="w-full bg-gray-200 dark:bg-grayDark-700 rounded-full h-2 relative">
                        <div
                            className={`h-2 rounded-full transition-all duration-300 ${isCompleted ? "bg-green-500" : "bg-primary"}`}
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>

                    {/* Ce qui attend l'utilisateur : sans ça, il ne sait pas
                        où il s'était arrêté et doit rouvrir le sommaire. */}
                    {showNextSession && (
                        <div className="mt-4 flex items-center gap-2.5">
                            <SolarIcon
                                name="play-circle-bold"
                                className="text-lg text-primary shrink-0"
                            />
                            <p className="mb-0 min-w-0 flex-1 truncate text-sm font-medium text-gray-700 dark:text-grayDark-200">
                                {nextSession.title}
                            </p>
                            {nextSession.remaining > 0 && (
                                <span className="hidden shrink-0 text-sm text-gray-500 dark:text-grayDark-400 sm:inline">
                                    {t("sessions_left", {
                                        count: nextSession.remaining,
                                    })}
                                </span>
                            )}
                        </div>
                    )}
                </CardContent>
            </Link>

            {/* Sur mobile, le bouton du coin haut-droit est masqué : sans ce
                relais, la carte n'affiche aucune action alors que reprendre
                une formation est la raison n°1 de venir. */}
            <div className="px-6 pb-6 lg:hidden">
                <Link
                    href={link}
                    className="btn-3d press-safe lip-orange block w-full rounded-xl bg-primary px-5 py-3 text-center text-sm font-medium text-white transition-all duration-200 hover:bg-primary/90 hover:!text-white"
                >
                    {actionLabel}
                </Link>
            </div>

            <div className="absolute right-3 top-7 flex items-center gap-2 z-10">
                {course.id && (course._kind === "buyer" || course._kind === "student") && (
                    <CourseCardMenu
                        entityId={course.id}
                        entityKind={course._kind}
                        isArchived={false}
                        onArchiveChange={onArchiveChange}
                    />
                )}
                <Link
                    href={link}
                    className="btn-3d press-safe lip-orange bg-primary hover:bg-primary/90 text-white hover:!text-white px-5 py-3 rounded-xl text-sm font-medium transition-all duration-200 hover:scale-105 lg:block hidden"
                >
                    {actionLabel}
                </Link>
            </div>
        </Card>
    );
};

// Composant pour les formations secondaires
const SecondaryCourseCard = ({ course, onArchiveChange }) => {
    const courseData = course.product || course.program;
    const progression = course.progression;
    const progressPercentage = progression?.total || 0;
    const isAccelerator = !course.product;
    const isCompleted = progressPercentage === 100;

    let link;
    if (
        courseData &&
        courseData.programConstructorVersion &&
        courseData.programConstructorVersion >= 2
    ) {
        // For program with constructor structure version >= 2
        link = `/accelerators/${courseData.slug}/dashboard`;
    } else if (course.category == "program") {
        if (course.slug == "le-programme-rocket-2020") {
            if (progression && progression.part) {
                link = `/course/${courseData.slug}/${progression.part}/${progression.position}`;
            } else {
                link = `/course/${courseData.slug}/1/1`;
            }
        } else {
            link = `/course/${courseData.slug}/dashboard`;
        }
    } else {
        if (progression && progression.part) {
            link = `/course/${courseData.slug}/${progression.part}/${progression.position}`;
        } else {
            link = `/course/${courseData.slug}/1/1`;
        }
    }

    return (
        <Card className={`special-card relative ${isCompleted ? "overflow-hidden" : ""}`}>
            <Link href={link} className="group block">
                <CardContent className="pt-5 relative">
                    {isCompleted && (
                        <div
                            className="absolute inset-0 pointer-events-none opacity-[0.03]"
                            style={{
                                backgroundImage: "repeating-linear-gradient(45deg, currentColor, currentColor 1px, transparent 1px, transparent 8px)"
                            }}
                        />
                    )}
                    <div className="flex items-center gap-3 relative">
                        <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0">
                            <Image
                                src={courseData?.image}
                                alt={courseData?.name}
                                width={48}
                                height={48}
                                className="object-cover w-full h-full"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h5 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight truncate group-hover:text-black group-hover:dark:text-white transition-colors mb-1 pr-7">
                                {courseData?.name}
                            </h5>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 bg-gray-200 dark:bg-grayDark-700 rounded-full h-1.5">
                                    <div
                                        className={`h-1.5 rounded-full transition-all duration-300 ${isCompleted ? "bg-green-500" : "bg-primary"}`}
                                        style={{
                                            width: `${progressPercentage}%`,
                                        }}
                                    />
                                </div>
                                <span className="text-sm text-gray-500 dark:text-grayDark-400 font-medium shrink-0 min-w-[35px]">
                                    {progressPercentage}%
                                </span>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Link>
            {course.id && (course._kind === "buyer" || course._kind === "student") && (
                <div className="absolute right-2 top-[18px] z-10">
                    <CourseCardMenu
                        entityId={course.id}
                        entityKind={course._kind}
                        isArchived={false}
                        onArchiveChange={onArchiveChange}
                        size="sm"
                    />
                </div>
            )}
        </Card>
    );
};

const EmptyState = () => {
    const t = useTranslations("CourseSection");
    return (
        <div className="bg-white dark:bg-bgDark-840 rounded-lg border border-gray-200 dark:border-borderDark p-6 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-white/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg
                    className="w-6 h-6 text-gray-400 dark:text-grayDark-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                </svg>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-base mb-2">
                {t("no_courses")}
            </h3>
            <p className="text-gray-600 dark:text-grayDark-400 text-sm mb-4">
                {t("start_learning")}
            </p>
            <Link href="/courses">
                <div className="btn-3d lip-orange bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg font-medium transition-colors text-sm inline-block">
                    {t("view_courses")}
                </div>
            </Link>
        </div>
    );
};

// Skeleton de chargement
const CourseSectionSkeleton = () => {
    const t = useTranslations("CourseSection");
    return (
        <div className="mt-6">
            <div className="mb-3">
                <h2 className="font-bold text-gray-900 dark:text-white text-xl">
                    {t("your_courses")}
                </h2>
            </div>
            <div className="space-y-3">
                {/* Skeleton formation principale */}
                <Card className="special-card">
                    <CardContent className="pt-5">
                        <div className="flex items-center gap-4 mb-4">
                            <Skeleton width={48} height={48} borderRadius={12} />
                            <div className="flex-1">
                                <Skeleton width="60%" height={22} className="mb-2" />
                                <Skeleton width="40%" height={16} />
                            </div>
                        </div>
                        <Skeleton height={8} borderRadius={9999} />
                    </CardContent>
                </Card>
                {/* Skeleton formation secondaire */}
                <Card className="special-card">
                    <CardContent className="pt-5">
                        <div className="flex items-center gap-3">
                            <Skeleton width={48} height={48} borderRadius={8} />
                            <div className="flex-1">
                                <Skeleton width="50%" height={16} className="mb-1" />
                                <div className="flex items-center gap-3">
                                    <Skeleton containerClassName="flex-1" height={6} borderRadius={9999} />
                                    <Skeleton width={35} height={16} />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

// Composant principal
export default function CourseSection({ allCourses, isLoading, onArchiveChange, archivedCount = 0 }) {
    const [showAll, setShowAll] = useState(false);
    const [isArchivesOpen, setIsArchivesOpen] = useState(false);
    const t = useTranslations("CourseSection");

    if (isLoading) {
        return <CourseSectionSkeleton />;
    }

    const hasArchives = archivedCount > 0;
    const archivesLink = hasArchives ? (
        <button
            type="button"
            onClick={() => setIsArchivesOpen(true)}
            title={t("see_archived")}
            aria-label={t("see_archived")}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-grayDark-400 hover:text-primary dark:hover:text-primary transition-colors mb-0"
        >
            <SolarIcon name="archive-linear" className="shrink-0 text-[14px]" />
            <span className="hidden sm:inline">{t("see_archived")}</span>
        </button>
    ) : null;

    if (!allCourses?.length) {
        return (
            <>
                <div className="mt-6">
                    <div className="mb-6 flex items-center justify-between gap-3">
                        <h2 className="font-semibold text-gray-900 dark:text-white text-xl">
                            {t("your_courses")}
                        </h2>
                        {archivesLink}
                    </div>
                    <EmptyState />
                </div>
                <ArchivedCoursesModal
                    isOpen={isArchivesOpen}
                    onClose={() => setIsArchivesOpen(false)}
                    onUnarchive={onArchiveChange}
                />
            </>
        );
    }

    const mainCourse = allCourses[0];
    const remainingCourses = allCourses.slice(1);
    const visibleSecondaryCourses = showAll
        ? remainingCourses
        : remainingCourses.slice(0, 2);
    const hasMoreCourses = remainingCourses.length > 2;

    return (
        <>
            <div className="mt-6">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <h2 className="font-bold text-gray-900 dark:text-white text-xl">
                        {allCourses.length > 1
                            ? t("your_courses")
                            : t("your_course")}
                    </h2>
                    <div className="flex items-center gap-3">
                        {archivesLink}
                        {allCourses.length > 1 && (
                            <>
                                {archivesLink && (
                                    <span className="h-4 w-px bg-gray-200 dark:bg-grayDark-700" />
                                )}
                                <span className="text-sm text-gray-500 dark:text-grayDark-400">
                                    {t("courses_number", {
                                        number: allCourses.length,
                                    })}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                <div className="lg:space-y-6 space-y-3">
                    <MainCourseCard course={mainCourse} onArchiveChange={onArchiveChange} />

                    {visibleSecondaryCourses.length > 0 && (
                        <div className="xl:space-y-3 space-y-1">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {visibleSecondaryCourses.map((course, index) => (
                                    <SecondaryCourseCard
                                        key={`${
                                            course.product?.id ||
                                            course.program?.id ||
                                            "course"
                                        }-${index}-${
                                            course.product?.slug ||
                                            course.program?.slug ||
                                            "unknown"
                                        }`}
                                        course={course}
                                        onArchiveChange={onArchiveChange}
                                    />
                                ))}
                            </div>

                            {hasMoreCourses && (
                                <div className="flex justify-center pt-2">
                                    <button
                                        onClick={() => setShowAll(!showAll)}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-grayDark-400 hover:text-primary dark:hover:text-primary transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-grayDark-800 rounded-lg"
                                    >
                                        {showAll ? (
                                            <>
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M5 15l7-7 7 7"
                                                    />
                                                </svg>
                                                {t("see_less")}
                                            </>
                                        ) : (
                                            <>
                                                <span>
                                                    {t("see_more_with_number", {
                                                        number:
                                                            remainingCourses.length -
                                                            2,
                                                    })}
                                                </span>
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 9l-7 7-7-7"
                                                    />
                                                </svg>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

            </div>

            <ArchivedCoursesModal
                isOpen={isArchivesOpen}
                onClose={() => setIsArchivesOpen(false)}
                onUnarchive={onArchiveChange}
            />
        </>
    );
}
